// Гайд «Как это работает» — мини-спринт C, уровень 3.
// Source: docs/specs/design-revision/c-onboarding.md §3 + тикет HEAL-63.
//
// Гайд Алины принят целиком, слово в слово: четыре шага, заголовки, пояснения,
// превью реальных фрагментов интерфейса, шапка с крестиком и точками
// прогресса, кнопки. Тексты и числа превью зафиксированы в HEAL-63 и меняются
// только вместе с тикетом.
//
// Отступление ровно одно — момент показа (D-9). Гайд не встаёт между
// регистрацией и первой загрузкой, а живёт в Профиле и на пустой Главной, для
// тех, кто придёт за ним сам. Причина: наша единственная активационная веха —
// первый загруженный анализ, и четыре экрана чтения встают ровно перед ней. К
// тому же три шага из четырёх показывают данные, которых у нового человека
// ещё нет, — после закрытия он попадал бы на пустой экран.
//
// Второе отступление, мелкое: последняя кнопка возвращает туда, откуда гайд
// открыли, а не в «Показатели», как сказано в тикете. При контекстной схеме
// гайд открывают осознанно, и увести человека в третье место — потеря
// контекста; для нового пользователя «Показатели» вдобавок пусты.

import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'wouter';
import { Check, Mail, Share2, Sparkles, UploadCloud, X } from 'lucide-react';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { IconTile } from '@/components/ui/icon-tile';
import { Surface } from '@/components/ui/surface';
import { SummaryBarChart } from '@/components/dashboard/SummaryBarChart';
import { MarkerRow } from '@/components/dashboard/MarkerRow';
import { BiomarkerChart } from '@/components/dashboard/BiomarkerChart';
import { clearGuideOrigin, readGuideOrigin } from '@/lib/guideNav';
import { cn } from '@/lib/utils';
import type { BiomarkerDetailPoint } from '@/api/types';

const STEP_COUNT = 4;

/**
 * Превью статичные, из «глупых» компонентов.
 *
 * Живые сюда встраивать нельзя: `BiomarkerSummaryCard` сам дёргает
 * `useBiomarkerSummary()`, а `AttentionList` требует данных дашборда. Гайд
 * открывается в том числе у человека без единого анализа — получили бы лишние
 * запросы и хрупкие состояния загрузки внутри обучающего экрана.
 */
const CHART_POINTS: BiomarkerDetailPoint[] = [
  { value_normalized: 137, value_qualitative: null, unit_normalized: 'г/л', measured_at: '2026-03-14', upload_id: 1, clinical_status: 'in_range', optimum_status: null, lab_source: null },
  { value_normalized: 139, value_qualitative: null, unit_normalized: 'г/л', measured_at: '2026-05-16', upload_id: 2, clinical_status: 'in_range', optimum_status: null, lab_source: null },
  { value_normalized: 141, value_qualitative: null, unit_normalized: 'г/л', measured_at: '2026-07-18', upload_id: 3, clinical_status: 'in_range', optimum_status: null, lab_source: null },
  { value_normalized: 143, value_qualitative: null, unit_normalized: 'г/л', measured_at: '2026-09-20', upload_id: 4, clinical_status: 'out_of_range', optimum_status: null, lab_source: null },
];

const SHARE_OPTIONS = [
  { icon: Share2, label: 'Поделиться графиком' },
  { icon: Mail, label: 'Отправить на почту' },
  { icon: UploadCloud, label: 'Сохранить файлом' },
];

type Step = { title: string; text: string; preview: ReactNode };

const STEPS: Step[] = [
  {
    title: 'Загрузите анализ удобным способом',
    text: 'PDF из почты, файл с телефона или фото бланка — распознаём сами.',
    preview: (
      <div className="rounded-surface bg-primary p-5 text-primary-foreground">
        <div className="mb-4 flex items-start gap-3">
          <IconTile size="md" tone="inverse">
            <UploadCloud className="h-6 w-6" aria-hidden="true" />
          </IconTile>
          <div>
            <div className="mb-1 text-lg font-semibold leading-tight">Загрузите первый анализ</div>
            <div className="text-sm leading-snug text-white/90">
              PDF из почты, файлов или фото — распознаём за 15–30 секунд
            </div>
          </div>
        </div>
        <div className="block w-full rounded-surface bg-surface py-3 text-center text-sm font-semibold text-primary-text">
          Загрузить анализ
        </div>
      </div>
    ),
  },
  {
    title: 'Сразу увидите общую картину',
    text: 'Все показатели разложены на группы: что в норме, что требует внимания, что вышло за границы.',
    preview: (
      <div className="space-y-3">
        <Surface className="p-4">
          <SummaryBarChart title="Сводка" total={98} normal={70} attention={5} outOfRange={3} unknown={20} />
        </Surface>
        <MarkerRow
          variant="attention"
          name="гемоглобин"
          normalRangeLabel="130–160 г/л"
          value={143}
          unit="г/л"
          clinicalStatus="out_of_range"
          direction="above"
        />
      </div>
    ),
  },
  {
    title: 'Следите за изменениями во времени',
    text: 'Каждый новый анализ добавляет точку на график — видно, куда двигается показатель.',
    preview: (
      <div className="space-y-3">
        <Surface className="p-3">
          <BiomarkerChart points={CHART_POINTS} normalMin={130} normalMax={160} unit="г/л" period="all" />
        </Surface>
        <MarkerRow
          variant="change"
          name="гемоглобин"
          value={143}
          prevValue={137}
          unit="г/л"
          delta={{ delta_value: 6, delta_pct: 4.4, delta_color: 'bad' }}
        />
      </div>
    ),
  },
  {
    title: 'Поделитесь результатами с врачом или близкими',
    text: 'Отправьте график или сводку — в удобном виде и ровно в том объёме, который выберете.',
    preview: (
      <div className="space-y-3">
        <Surface className="divide-y divide-border">
          {SHARE_OPTIONS.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-3 px-3.5 py-3">
              <IconTile size="sm">
                <Icon className="h-5 w-5" aria-hidden="true" />
              </IconTile>
              <span className="text-hl-body font-semibold text-foreground">{label}</span>
            </div>
          ))}
        </Surface>
        <div className="flex items-start gap-2 rounded-surface bg-muted p-3 text-hl-small text-muted-foreground">
          <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary-text" aria-hidden="true" />
          Ничего не отправляется без вашего подтверждения
        </div>
      </div>
    ),
  },
];

export function Guide() {
  const [, setLocation] = useLocation();
  // Гайд всегда стартует с шага 1, как в тикете: прогресс не запоминается.
  const [step, setStep] = useState(0);

  // Источник читается один раз при монтировании и тут же сбрасывается: иначе
  // следующий заход — по прямой ссылке или после обновления — унаследовал бы
  // чужое значение. Чтение чистое, сброс в эффекте.
  const [origin] = useState(readGuideOrigin);
  useEffect(() => clearGuideOrigin, []);

  const exit = () => setLocation(origin);
  const isLast = step === STEP_COUNT - 1;
  const current = STEPS[step];

  return (
    <MobileFrame>
      <header className="flex flex-shrink-0 items-center gap-3 border-b border-border px-4 py-3">
        <button
          type="button"
          onClick={exit}
          aria-label="Закрыть гайд"
          className="-ml-2 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="flex flex-1 items-center justify-center gap-1.5" aria-hidden="true">
          {STEPS.map((s, i) => (
            <span
              key={s.title}
              className={cn(
                'h-1.5 rounded-full transition-all',
                i === step ? 'w-5 bg-primary' : 'w-1.5 bg-border',
              )}
            />
          ))}
        </div>
        <span className="w-9 flex-shrink-0 text-right text-hl-small font-semibold text-muted-foreground">
          {step + 1}/{STEP_COUNT}
        </span>
      </header>

      <main className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-5">
        <div>
          <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-hl-small font-medium text-primary-text">
            <Sparkles className="h-3 w-3" aria-hidden="true" />
            Как это работает
          </div>
          <h1 className="mb-2 text-hl-title font-bold leading-tight">{current.title}</h1>
          <p className="text-sm leading-relaxed text-muted-foreground">{current.text}</p>
        </div>

        {/* Превью — картинка интерфейса, а не сам интерфейс: внутри ничего не
            нажимается и ни один запрос не уходит. */}
        <div aria-hidden="true" className="pointer-events-none select-none">
          {current.preview}
        </div>
      </main>

      <div className="flex-shrink-0 space-y-2 border-t border-border px-5 py-4">
        <button
          type="button"
          onClick={() => (isLast ? exit() : setStep(step + 1))}
          className="block w-full rounded-control bg-primary py-3 text-sm font-semibold text-primary-foreground"
        >
          {isLast ? 'Начать пользоваться' : 'Далее'}
        </button>
        {!isLast && (
          <button
            type="button"
            onClick={exit}
            className="block w-full py-2 text-sm font-medium text-muted-foreground"
          >
            Пропустить
          </button>
        )}
      </div>
    </MobileFrame>
  );
}
